import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import type { Deck, Share, ShareSummary, ShareTtl } from '../api/types';
import { api } from '../api/client';
import { navigate } from '../lib/router-state';
import { generatePasscode } from '../lib/share-viewer';

/**
 * Format an ISO timestamp as "12 May 2026" — falls back gracefully.
 */
function fmtDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Format an expiry timestamp as "Expires 5 June 2026" or "Never expires".
 * Used in the Manage Shares list so the owner can scan at a glance which
 * link is going to die when.
 */
function fmtExpiry(iso: string | null): string {
  if (!iso) return 'Never expires';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return `Expires ${iso}`;
  return `Expires ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`;
}

function showToast(message: string) {
  const el = document.createElement('div');
  el.className = 'toast';
  el.textContent = message;
  document.body.appendChild(el);
  window.setTimeout(() => el.remove(), 2400);
}

// TTL chip definitions — kept as a const tuple so the label, value, and order
// live in one place. The order is intentional: shortest-lived first so the
// safe defaults sit at the start of the menu (the "Never" option is opt-in).
const TTL_CHOICES: ReadonlyArray<{ value: ShareTtl; label: string }> = [
  { value: '1d',    label: '1 day'   },
  { value: '7d',    label: '7 days'  },
  { value: '30d',   label: '30 days' },
  { value: 'never', label: 'Never'   },
];

/**
 * One deck preview in the list grid. The card surfaces a live cover-slide
 * thumbnail rendered via the same iframe srcdoc pipeline as the viewer:
 *
 *   - The iframe loads the deck at its design size (1920x1080) and is
 *     scaled down via CSS transform to fit the card's 16:9 preview box.
 *   - `pointer-events: none` on the iframe so clicks land on the parent
 *     `.preview` div which routes to the viewer.
 *   - deck-bridge.js (loaded inside the deck HTML) receives
 *     `{type:'deck:mode', mode:'thumb'}` and `{type:'deck:theme', theme}`
 *     on iframe load, and re-applies the theme on every `themechange`
 *     event so toggling the parent's theme re-themes every card.
 *
 * Sandbox stays consistent with the viewer:
 * `allow-scripts allow-popups allow-forms allow-modals` (no
 * `allow-same-origin` — deck-bridge.js compensates via postMessage).
 *
 * Phase C — the Share button now opens a small inline menu with four TTL
 * chips plus a "Manage shares" sub-action for the owner's revocation flow.
 * The menu is positioned absolutely inside the card so no floating-UI
 * dependency is required; the @state() flags drive everything.
 */
@customElement('deck-card')
export class DeckCard extends LitElement {
  @property({ attribute: false }) deck!: Deck;
  /** True while a `createShare` request is in flight. Disables every chip
   *  so a double-click can't mint two shares. */
  @state() private sharing = false;
  /** True when the inline TTL chip menu is open. */
  @state() private shareMenuOpen = false;
  /** True when the Manage shares list is expanded (within the share menu). */
  @state() private managingShares = false;
  /** Last fetched list of active shares. null = "not yet loaded"; [] = "no
   *  shares". The handler converts null → empty list when rendering. */
  @state() private shares: ShareSummary[] | null = null;
  /** True while the listShares request is in flight. */
  @state() private loadingShares = false;
  /** Tokens currently being revoked — keyed so the row can render a
   *  disabled state without affecting the others. */
  @state() private revokingTokens = new Set<string>();
  /** Sharing v2: the passcode the owner has typed (or generated) for the next
   *  share. Empty = unprotected. */
  @state() private passcodeInput = '';
  /** Sharing v2: after a protected share is minted, the create response is held
   *  here so the menu can REVEAL the link + the one-time passcode. null = no
   *  reveal pending. */
  @state() private revealShare: Share | null = null;
  @query('iframe') private iframeEl?: HTMLIFrameElement;

  static styles = css`
    :host {
      display: block;
      background: var(--surface);
      border: 1px solid var(--border-subtle);
      border-radius: 12px;
      overflow: hidden;
      transition: transform 120ms, border-color 120ms, box-shadow 120ms;
      position: relative;
    }
    :host(:hover) {
      transform: translateY(-2px);
      border-color: var(--border-default);
      box-shadow: var(--shadow-hover);
    }
    .preview {
      position: relative;
      width: 100%;
      aspect-ratio: 16 / 9;
      background: var(--canvas);
      cursor: pointer;
      overflow: hidden;
      border-bottom: 1px solid var(--border-subtle);
    }
    /* Iframe is rendered at the design size (1920x1080) then scaled
       down to fit the card via transform. The container clips the
       overflow so only the cover slide shows. Pointer events disabled
       so the click bubbles to the parent .preview which navigates. */
    .preview iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 1920px;
      height: 1080px;
      border: 0;
      transform-origin: top left;
      pointer-events: none;
      background: var(--canvas);
    }
    .body {
      padding: 16px 20px 20px;
      position: relative;
    }
    h3 {
      font-family: var(--font-editorial, Georgia, serif);
      font-size: 1.25rem;
      font-weight: 500;
      letter-spacing: -0.012em;
      margin: 0 0 6px;
      color: var(--text-primary);
      line-height: 1.25;
    }
    p.subtitle {
      color: var(--text-secondary);
      font-size: 0.9rem;
      margin: 0 0 12px;
      line-height: 1.4;
      min-height: 1.4em;
    }
    .row {
      display: flex;
      gap: 10px;
      margin-top: 12px;
    }
    .row button {
      flex: 0 0 auto;
      background: var(--surface-hover);
      border: 1px solid var(--border-subtle);
      color: var(--text-primary);
      font-size: 0.85rem;
      padding: 7px 14px;
      border-radius: 6px;
      cursor: pointer;
      transition: background 120ms, border-color 120ms;
    }
    .row button:hover {
      background: var(--ink-soft);
      border-color: var(--border-default);
    }
    .row button[disabled] { opacity: 0.6; cursor: not-allowed; }
    .meta {
      display: flex;
      justify-content: space-between;
      margin-top: 14px;
      padding-top: 12px;
      border-top: 1px solid var(--border-subtle);
      color: var(--text-tertiary);
      font-size: 0.8rem;
    }
    /* Phase C — Share menu. The menu replaces the standard Share button when
       open; chips are rendered as small inline-pill buttons reusing the
       existing nav-link visual language (transparent surface, hover ->
       surface-hover, active state -> ink). */
    .share-menu {
      margin-top: 12px;
      padding: 12px;
      border: 1px solid var(--border-subtle);
      border-radius: 8px;
      background: var(--canvas);
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .share-menu .menu-head {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.85rem;
      color: var(--text-secondary);
    }
    .share-menu .menu-head .close {
      background: transparent;
      border: 1px solid transparent;
      color: var(--text-tertiary);
      cursor: pointer;
      font-size: 0.85rem;
      padding: 2px 8px;
      border-radius: 6px;
    }
    .share-menu .menu-head .close:hover {
      color: var(--text-primary);
      background: var(--surface-hover);
    }
    .chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }
    .chip {
      background: transparent;
      border: 1px solid var(--border-subtle);
      color: var(--text-primary);
      font-size: 0.85rem;
      padding: 8px 12px;
      border-radius: 6px;
      cursor: pointer;
      transition: background 120ms, border-color 120ms, color 120ms;
    }
    .chip:hover {
      background: var(--surface-hover);
      border-color: var(--border-default);
    }
    .chip:active,
    .chip[aria-pressed='true'] {
      background: var(--ink);
      color: var(--canvas);
      border-color: var(--ink);
    }
    .chip[disabled] { opacity: 0.55; cursor: not-allowed; }
    .manage-link {
      background: transparent;
      border: 0;
      padding: 4px 0;
      font-size: 0.8rem;
      color: var(--text-secondary);
      cursor: pointer;
      text-align: left;
      align-self: flex-start;
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    .manage-link:hover { color: var(--text-primary); }
    .shares-list {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .shares-list li {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;
      padding: 8px 10px;
      border: 1px solid var(--border-subtle);
      border-radius: 6px;
      background: var(--surface);
    }
    .shares-list .share-meta {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }
    .shares-list .share-when {
      font-size: 0.75rem;
      color: var(--text-tertiary);
    }
    .shares-list .share-expiry {
      font-size: 0.78rem;
      color: var(--text-secondary);
    }
    .shares-list .revoke {
      flex: 0 0 auto;
      background: transparent;
      border: 1px solid var(--border-subtle);
      color: var(--text-primary);
      font-size: 0.78rem;
      padding: 4px 10px;
      border-radius: 6px;
      cursor: pointer;
    }
    .shares-list .revoke:hover {
      background: var(--surface-hover);
      border-color: var(--border-default);
    }
    .shares-list .revoke[disabled] { opacity: 0.55; cursor: not-allowed; }
    .empty {
      font-size: 0.8rem;
      color: var(--text-tertiary);
      padding: 4px 0;
    }

    /* ─── Sharing v2 — passcode create sub-block ──────────────────────── */
    .passcode-block {
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding: 8px 0 2px;
      border-top: 1px solid var(--border-subtle);
    }
    .passcode-block .pc-label {
      font-size: 0.78rem;
      color: var(--text-secondary);
    }
    .passcode-row {
      display: flex;
      gap: 6px;
      align-items: center;
    }
    .passcode-row input {
      flex: 1 1 auto;
      min-width: 0;
      font: inherit;
      font-size: 0.85rem;
      color: var(--text-primary);
      background: var(--surface);
      border: 1px solid var(--border-subtle);
      border-radius: 6px;
      padding: 7px 10px;
    }
    .passcode-row input:focus {
      outline: none;
      border-color: var(--text-secondary);
    }
    .passcode-row .gen {
      flex: 0 0 auto;
      background: transparent;
      border: 1px solid var(--border-subtle);
      color: var(--text-primary);
      font-size: 0.8rem;
      padding: 7px 10px;
      border-radius: 6px;
      cursor: pointer;
    }
    .passcode-row .gen:hover {
      background: var(--surface-hover);
      border-color: var(--border-default);
    }
    .pc-hint {
      font-size: 0.75rem;
      color: var(--text-tertiary);
      line-height: 1.4;
      margin: 0;
    }

    /* ─── Sharing v2 — reveal state (one-time passcode) ───────────────── */
    .reveal {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .reveal .reveal-warn {
      font-size: 0.8rem;
      color: var(--text-primary);
      line-height: 1.45;
      padding: 8px 10px;
      border: 1px solid var(--border-default);
      border-radius: 6px;
      background: var(--surface-hover);
    }
    .reveal .reveal-field {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .reveal .reveal-field .rf-label {
      font-size: 0.72rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--text-tertiary);
    }
    .reveal .reveal-field .rf-row {
      display: flex;
      gap: 6px;
      align-items: center;
    }
    .reveal .reveal-field .rf-value {
      flex: 1 1 auto;
      min-width: 0;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 0.82rem;
      color: var(--text-primary);
      background: var(--surface);
      border: 1px solid var(--border-subtle);
      border-radius: 6px;
      padding: 7px 10px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .reveal .reveal-field .copy {
      flex: 0 0 auto;
      background: transparent;
      border: 1px solid var(--border-subtle);
      color: var(--text-primary);
      font-size: 0.8rem;
      padding: 7px 12px;
      border-radius: 6px;
      cursor: pointer;
    }
    .reveal .reveal-field .copy:hover {
      background: var(--surface-hover);
      border-color: var(--border-default);
    }
    .reveal .reveal-done {
      align-self: flex-start;
      background: var(--ink);
      color: var(--canvas);
      border: 1px solid var(--ink);
      font-size: 0.82rem;
      padding: 7px 14px;
      border-radius: 6px;
      cursor: pointer;
    }

    /* ─── Sharing v2 — Protected badge in Manage shares ───────────────── */
    .protected-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 0.7rem;
      font-weight: 600;
      letter-spacing: 0.02em;
      color: var(--text-secondary);
      border: 1px solid var(--border-subtle);
      border-radius: 999px;
      padding: 1px 8px;
      margin-top: 2px;
      align-self: flex-start;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('themechange', this.onThemeChange);
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('themechange', this.onThemeChange);
    window.removeEventListener('resize', this.onResize);
  }

  private currentTheme(): 'light' | 'dark' {
    const t = document.documentElement.getAttribute('data-theme');
    return t === 'dark' ? 'dark' : 'light';
  }

  private onThemeChange = () => {
    this.postToIframe({ type: 'deck:theme', theme: this.currentTheme() });
  };

  private onResize = () => this.fitIframe();

  private onIframeLoad = () => {
    // Order matters: thumb mode first (hides chrome + rail), then theme.
    this.postToIframe({ type: 'deck:mode', mode: 'thumb' });
    this.postToIframe({ type: 'deck:theme', theme: this.currentTheme() });
    this.fitIframe();
  };

  private postToIframe(msg: unknown) {
    try {
      this.iframeEl?.contentWindow?.postMessage(msg, '*');
    } catch (e) { /* defensive */ }
  }

  private fitIframe() {
    const iframe = this.iframeEl;
    if (!iframe) return;
    const container = iframe.parentElement;
    if (!container) return;
    const containerWidth = container.clientWidth;
    if (!containerWidth) return;
    const scale = containerWidth / 1920;
    iframe.style.transform = `scale(${scale})`;
  }

  private open(e: Event) {
    e.preventDefault();
    const href = `/decks/${encodeURIComponent(this.deck.id)}`;
    // Funnel through the router-state singleton so nav-bar + any
    // other subscribers update in the same notify cycle.
    navigate(href);
  }

  private toggleShareMenu(e: Event) {
    e.stopPropagation();
    this.shareMenuOpen = !this.shareMenuOpen;
    if (this.shareMenuOpen) {
      // Fresh create state.
      this.passcodeInput = '';
      this.revealShare = null;
    } else {
      // Collapse the management sub-pane too so the next open starts clean.
      this.managingShares = false;
      this.revealShare = null;
    }
  }

  private closeShareMenu(e?: Event) {
    if (e) e.stopPropagation();
    this.shareMenuOpen = false;
    this.managingShares = false;
    this.revealShare = null;
    this.passcodeInput = '';
  }

  /** Fill the passcode input with a freshly generated unambiguous code. */
  private genPasscode(e: Event) {
    e.stopPropagation();
    this.passcodeInput = generatePasscode();
  }

  private async pickTtl(ttl: ShareTtl, e: Event) {
    e.stopPropagation();
    if (this.sharing) return;

    // The "Never" option is opt-in by explicit confirm — a permanent link is
    // a sharp tool and the FE makes sure the owner asked for it.
    if (ttl === 'never') {
      const proceed = window.confirm(
        'Permanent share — anyone with this link can view the deck forever. Continue?',
      );
      if (!proceed) return;
    }

    this.sharing = true;
    try {
      const passcode = this.passcodeInput.trim();
      const share = await api.createShare(
        this.deck.id,
        ttl,
        passcode ? { passcode } : undefined,
      );
      // Invalidate the cached list so a subsequent Manage Shares open
      // reflects the freshly minted entry.
      this.shares = null;

      if (share.passcode) {
        // PROTECTED — HOLD the menu open in a reveal state. The owner copies the
        // link AND the one-time passcode; we deliberately do NOT auto-close or
        // auto-copy (the passcode must be seen + sent separately).
        this.revealShare = share;
        return;
      }

      // UNPROTECTED — preserve V1 behaviour: copy the link and close.
      const full = this.absoluteUrl(share.url);
      try {
        await navigator.clipboard.writeText(full);
        showToast('Share link copied');
      } catch {
        // Clipboard may be blocked in non-secure contexts — fall back to a prompt.
        window.prompt('Copy the share link below', full);
      }
      this.closeShareMenu();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Share failed';
      showToast(`Share failed: ${msg}`);
    } finally {
      this.sharing = false;
    }
  }

  private absoluteUrl(url: string): string {
    return url.startsWith('http') ? url : `${window.location.origin}${url}`;
  }

  private async copyText(text: string, label: string, e: Event) {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label} copied`);
    } catch {
      window.prompt(`Copy the ${label.toLowerCase()} below`, text);
    }
  }

  private async openManage(e: Event) {
    e.stopPropagation();
    this.managingShares = true;
    await this.loadShares();
  }

  private async loadShares() {
    if (this.loadingShares) return;
    this.loadingShares = true;
    try {
      this.shares = await api.listShares(this.deck.id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'List failed';
      showToast(`Failed to load shares: ${msg}`);
      this.shares = [];
    } finally {
      this.loadingShares = false;
    }
  }

  private async revoke(token: string, e: Event) {
    e.stopPropagation();
    if (this.revokingTokens.has(token)) return;
    this.revokingTokens = new Set([...this.revokingTokens, token]);
    try {
      await api.deleteShare(this.deck.id, token);
      // Optimistic update — drop the row locally without a re-fetch.
      this.shares = (this.shares ?? []).filter(s => s.token !== token);
      showToast('Share revoked');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Revoke failed';
      showToast(`Revoke failed: ${msg}`);
      // Fall back to a re-fetch so the UI re-syncs with reality.
      await this.loadShares();
    } finally {
      const next = new Set(this.revokingTokens);
      next.delete(token);
      this.revokingTokens = next;
    }
  }

  /** Navigate to the full-page analytics view for this deck. The analytics
   *  UI moved out of an inline card panel into its own owner-gated route. */
  private openAnalytics(e: Event) {
    e.stopPropagation();
    navigate(`/decks/${encodeURIComponent(this.deck.id)}/analytics`);
  }

  private renderShareMenu() {
    const headLabel = this.revealShare
      ? 'Share created'
      : this.managingShares
        ? 'Manage shares'
        : 'Share for...';
    return html`
      <div class="share-menu" @click=${(e: Event) => e.stopPropagation()}>
        <div class="menu-head">
          <span>${headLabel}</span>
          <button type="button" class="close" @click=${this.closeShareMenu}>Close</button>
        </div>
        ${this.revealShare
          ? this.renderReveal()
          : this.managingShares
            ? this.renderManage()
            : this.renderChips()}
      </div>
    `;
  }

  private renderChips() {
    return html`
      <div class="chips">
        ${TTL_CHOICES.map(choice => html`
          <button
            type="button"
            class="chip"
            ?disabled=${this.sharing}
            @click=${(e: Event) => this.pickTtl(choice.value, e)}
          >${choice.label}</button>
        `)}
      </div>
      <div class="passcode-block">
        <span class="pc-label">Passcode (optional)</span>
        <div class="passcode-row">
          <input
            type="text"
            autocomplete="off"
            placeholder="Type a passcode, or generate one"
            .value=${this.passcodeInput}
            ?disabled=${this.sharing}
            @click=${(e: Event) => e.stopPropagation()}
            @input=${(e: Event) => (this.passcodeInput = (e.target as HTMLInputElement).value)}
          />
          <button
            type="button"
            class="gen"
            ?disabled=${this.sharing}
            @click=${this.genPasscode}
          >Generate</button>
        </div>
        <p class="pc-hint">
          Add a passcode to protect this share. You'll see it once after creating
          — send it to viewers separately from the link.
        </p>
      </div>
      <button type="button" class="manage-link" @click=${this.openManage}>
        Manage shares
      </button>
    `;
  }

  /** Reveal state after a PROTECTED share is minted — shows the link AND the
   *  one-time passcode with copy buttons. Held open until the owner dismisses. */
  private renderReveal() {
    const share = this.revealShare;
    if (!share) return nothing;
    const full = this.absoluteUrl(share.url);
    const passcode = share.passcode ?? '';
    return html`
      <div class="reveal">
        <div class="reveal-warn">
          Shown once — send the passcode to viewers separately from the link.
        </div>
        <div class="reveal-field">
          <span class="rf-label">Share link</span>
          <div class="rf-row">
            <span class="rf-value" title=${full}>${full}</span>
            <button
              type="button"
              class="copy"
              @click=${(e: Event) => this.copyText(full, 'Link', e)}
            >Copy</button>
          </div>
        </div>
        <div class="reveal-field">
          <span class="rf-label">Passcode</span>
          <div class="rf-row">
            <span class="rf-value">${passcode}</span>
            <button
              type="button"
              class="copy"
              @click=${(e: Event) => this.copyText(passcode, 'Passcode', e)}
            >Copy</button>
          </div>
        </div>
        <button type="button" class="reveal-done" @click=${this.closeShareMenu}>Done</button>
      </div>
    `;
  }

  private renderManage() {
    if (this.loadingShares && this.shares === null) {
      return html`<div class="empty">Loading…</div>`;
    }
    const list = this.shares ?? [];
    if (list.length === 0) {
      return html`<div class="empty">No active shares.</div>`;
    }
    return html`
      <ul class="shares-list">
        ${list.map(share => {
          const revoking = this.revokingTokens.has(share.token);
          return html`
            <li>
              <div class="share-meta">
                <span class="share-when">Created ${fmtDate(share.createdAt)}</span>
                <span class="share-expiry">${fmtExpiry(share.expiresAt)}</span>
                ${share.protected
                  ? html`<span class="protected-badge">Protected</span>`
                  : nothing}
              </div>
              <button
                type="button"
                class="revoke"
                ?disabled=${revoking}
                @click=${(e: Event) => this.revoke(share.token, e)}
              >${revoking ? 'Revoking…' : 'Revoke'}</button>
            </li>
          `;
        })}
      </ul>
    `;
  }

  render() {
    const d = this.deck;
    return html`
      <div class="preview" @click=${this.open}>
        <iframe
          title=${d.title}
          srcdoc=${d.html}
          sandbox="allow-scripts allow-popups allow-forms allow-modals"
          @load=${this.onIframeLoad}
        ></iframe>
      </div>
      <div class="body">
        <h3>${d.title || 'Untitled deck'}</h3>
        ${d.subtitle ? html`<p class="subtitle">${d.subtitle}</p>` : html`<p class="subtitle"></p>`}
        <div class="row">
          <button type="button" @click=${this.open}>Open</button>
          <button
            type="button"
            @click=${this.toggleShareMenu}
            ?disabled=${this.sharing}
            aria-expanded=${this.shareMenuOpen}
          >${this.shareMenuOpen ? 'Cancel' : 'Share'}</button>
          <button
            type="button"
            @click=${this.openAnalytics}
          >Analytics</button>
        </div>
        ${this.shareMenuOpen ? this.renderShareMenu() : nothing}
        <div class="meta">
          <span>${d.author ?? 'Workplace by Ricoh'}</span>
          <span>Updated ${fmtDate(d.updatedAt)}</span>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'deck-card': DeckCard;
  }
}
